/**
 * RNCM Productivity Futuristic Badge Components
 * Neon glowing badges with vibrant colors
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { getTeamColor } from './constants';

export const TeamBadge = ({ team }) => {
  const color = getTeamColor(team);
  return (
    <Badge 
      className={`${color.bg} ${color.text} border-0 shadow-lg ${color.glow} px-3 py-1 font-semibold tracking-wide animate-pulse-subtle`}
      style={{ 
        boxShadow: `0 0 15px ${color.hex}40, 0 0 30px ${color.hex}20`,
        textShadow: team === 'Yellow' ? 'none' : '0 0 10px rgba(255,255,255,0.5)'
      }}
    >
      {team || 'N/A'}
    </Badge>
  );
};

export const OutcomeBadge = ({ outcome }) => {
  const styles = {
    'Within Threshold': {
      bg: 'bg-gradient-to-r from-emerald-400 to-green-500',
      glow: '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.3)'
    },
    'Above Threshold': {
      bg: 'bg-gradient-to-r from-cyan-400 to-blue-500',
      glow: '0 0 20px rgba(6, 182, 212, 0.6), 0 0 40px rgba(6, 182, 212, 0.3)'
    },
    'Below Threshold': {
      bg: 'bg-gradient-to-r from-rose-500 to-red-600',
      glow: '0 0 20px rgba(244, 63, 94, 0.6), 0 0 40px rgba(244, 63, 94, 0.3)'
    },
    'Over Capacity': {
      bg: 'bg-gradient-to-r from-amber-400 to-orange-500',
      glow: '0 0 20px rgba(251, 191, 36, 0.6), 0 0 40px rgba(251, 191, 36, 0.3)'
    },
    'Under Capacity': {
      bg: 'bg-gradient-to-r from-fuchsia-500 to-purple-600',
      glow: '0 0 20px rgba(217, 70, 239, 0.6), 0 0 40px rgba(217, 70, 239, 0.3)'
    }
  };

  const style = styles[outcome] || { bg: 'bg-gradient-to-r from-slate-400 to-slate-600', glow: 'none' };

  return (
    <Badge 
      className={`${style.bg} text-white border-0 px-3 py-1 font-semibold tracking-wide`}
      style={{ boxShadow: style.glow, textShadow: '0 0 10px rgba(255,255,255,0.5)' }}
    >
      {outcome || 'N/A'}
    </Badge>
  );
};

export const CapacityBadge = ({ capacity, threshold = 15 }) => {
  let style;
  let label;
  
  if (capacity >= threshold + 5) {
    style = { bg: 'bg-gradient-to-r from-red-500 to-rose-600', glow: '0 0 25px rgba(239, 68, 68, 0.7)' };
    label = 'Critical';
  } else if (capacity >= threshold + 2) {
    style = { bg: 'bg-gradient-to-r from-orange-400 to-amber-500', glow: '0 0 25px rgba(251, 146, 60, 0.7)' };
    label = 'High';
  } else if (capacity >= threshold) {
    style = { bg: 'bg-gradient-to-r from-yellow-400 to-amber-400', glow: '0 0 25px rgba(250, 204, 21, 0.7)' };
    label = 'Warning';
  } else {
    style = { bg: 'bg-gradient-to-r from-emerald-400 to-teal-500', glow: '0 0 25px rgba(52, 211, 153, 0.7)' };
    label = 'Normal';
  }

  return (
    <Badge 
      className={`${style.bg} ${capacity >= threshold ? 'text-black' : 'text-white'} border-0 px-3 py-1 font-bold`}
      style={{ boxShadow: style.glow }}
    >
      {label} ({capacity})
    </Badge>
  );
};

export const VisitsBadge = ({ visits, min, max }) => {
  let style;
  let label;

  if (visits < min) {
    style = { bg: 'bg-gradient-to-r from-rose-500 to-red-600', glow: '0 0 20px rgba(244, 63, 94, 0.6)' };
    label = 'Below';
  } else if (visits > max) {
    style = { bg: 'bg-gradient-to-r from-cyan-400 to-blue-500', glow: '0 0 20px rgba(6, 182, 212, 0.6)' };
    label = 'Above';
  } else {
    style = { bg: 'bg-gradient-to-r from-emerald-400 to-green-500', glow: '0 0 20px rgba(16, 185, 129, 0.6)' };
    label = 'On Target';
  }

  return (
    <Badge 
      className={`${style.bg} text-white border-0 px-3 py-1 font-semibold`}
      style={{ boxShadow: style.glow, textShadow: '0 0 8px rgba(255,255,255,0.4)' }}
    >
      {label} ({visits})
    </Badge>
  );
};

// New futuristic status indicator
export const StatusPulse = ({ status = 'active', size = 'md' }) => {
  const sizes = { sm: 'w-2 h-2', md: 'w-3 h-3', lg: 'w-4 h-4' };
  const colors = {
    active: 'bg-emerald-400',
    warning: 'bg-amber-400',
    critical: 'bg-rose-500',
    inactive: 'bg-slate-400'
  };

  return (
    <span className="relative flex">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${colors[status]} opacity-75`}></span>
      <span className={`relative inline-flex rounded-full ${sizes[size]} ${colors[status]}`}></span>
    </span>
  );
};
